import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  IconButton,
  Chip,
  Skeleton,
  Tooltip
} from "@mui/material";
import { 
  Search, 
  FilterList,
  Visibility,
  TrendingUp,
  TrendingDown,
  TrendingFlat
} from "@mui/icons-material";
import { formatBRL, formatNumber } from "../metrics/utils/formatters";
import moment from "moment";

const headerCellStyle = {
  fontWeight: 600,
  fontSize: "0.875rem",
  color: "#495057",
  textAlign: "left",
  backgroundColor: "#f8f9fa",
  padding: "12px 8px",
  borderBottom: "1px solid #e9ecef",
};

const cellStyle = {
  fontSize: "12px",
  padding: "8px",
  color: "#2c3e50",
  textAlign: "left",
  minWidth: "100px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

export default function ProductReferencesTable({ 
  data, 
  isLoading, 
  pagination, 
  onSelectProduct,
  onPageChange,
  onLimitChange,
  onFiltersChange,
  filters,
  selectedProduct
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const getTrendIcon = (percentDifference) => {
    const diff = percentDifference || 0;
    if (diff > 10) return <TrendingUp sx={{ color: '#f44336', fontSize: 16 }} />;
    if (diff < -10) return <TrendingDown sx={{ color: '#4caf50', fontSize: 16 }} />;
    return <TrendingFlat sx={{ color: '#2196f3', fontSize: 16 }} />;
  };

  const getStatusChip = (status) => {
    if (!status) return 'N/A';
    if (status.includes('high')) return 'Alto';
    if (status.includes('low')) return 'Baixo';
    if (status.includes('benchmark')) return 'OK';
    return 'N/A';
  };

  const getStatusColor = (status) => {
    if (!status) return 'default';
    if (status.includes('high')) return 'error';
    if (status.includes('low')) return 'success';
    if (status.includes('benchmark')) return 'primary';
    return 'default';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return moment(dateString, 'DD-MM-YYYY HH:mm:ss').format('DD/MM/YYYY');
    } catch {
      return '-';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    try {
      return moment(dateString, 'DD-MM-YYYY HH:mm:ss').format('DD/MM/YYYY HH:mm:ss');
    } catch {
      return '-';
    }
  };

  const handleSearch = () => {
    onFiltersChange({
      ...filters,
      search: searchTerm
    });
  };

  const handleFilterChange = (filterKey, value) => {
    onFiltersChange({
      ...filters,
      [filterKey]: value
    });
  };

  const renderSkeletonRow = () => (
    <TableRow>
      <TableCell><Skeleton variant="text" width={60} /></TableCell>
      <TableCell><Skeleton variant="text" width={50} /></TableCell>
      <TableCell><Skeleton variant="text" width={50} /></TableCell>
      <TableCell><Skeleton variant="text" width={50} /></TableCell>
      <TableCell><Skeleton variant="text" width={40} /></TableCell>
      <TableCell><Skeleton variant="text" width={40} /></TableCell>
      <TableCell><Skeleton variant="text" width={30} /></TableCell>
      <TableCell><Skeleton variant="text" width={40} /></TableCell>
      <TableCell><Skeleton variant="text" width={50} /></TableCell>
      <TableCell><Skeleton variant="text" width={40} /></TableCell>
      <TableCell><Skeleton variant="rectangular" width={24} height={24} /></TableCell>
    </TableRow>
  );

  return (
    <Card sx={{ 
      boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 4px 16px rgba(0,0,0,0.04)',
      borderRadius: '16px',
      border: '1px solid rgba(0,0,0,0.06)',
      mb: 4
    }}>
      <CardContent sx={{ p: { xs: 2, md: 3 } }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
            Lista de Produtos
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Buscar por ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 18, color: '#64748b' }} />
                  </InputAdornment>
                ),
                sx: { 
                  borderRadius: '10px',
                  fontSize: '0.875rem'
                }
              }}
              sx={{ width: 200 }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowFilters(!showFilters)}
              startIcon={<FilterList />}
              sx={{
                borderColor: '#e2e8f0',
                color: '#475569',
                borderRadius: '8px'
              }}
            >
              Filtros
            </Button>
          </Box>
        </Box>

        {/* Filtros */}
        {showFilters && (
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            mb: 3, 
            p: 2, 
            backgroundColor: '#f8fafc',
            borderRadius: '12px',
            flexWrap: 'wrap'
          }}>
            <TextField
              size="small"
              label="Preço Mín"
              type="number"
              value={filters.minPrice || ''}
              onChange={(e) => handleFilterChange('minPrice', e.target.value)}
              sx={{ minWidth: 150 }}
            />
            <TextField
              size="small"
              label="Preço Máx"
              type="number"
              value={filters.maxPrice || ''}
              onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
              sx={{ minWidth: 150 }}
            />
          </Box>
        )}

        {/* Tabela */}
        <TableContainer 
          component={Paper} 
          sx={{
            boxShadow: "0 1px 3px rgba(0,0,0,0.05), 0 4px 16px rgba(0,0,0,0.04)",
            borderRadius: '12px',
            border: '1px solid #e9ecef',
            borderTop: '2px solid #ffcc80',
            maxHeight: '600px',
            overflow: 'auto',
            width: '100%',
            minWidth: '1200px'
          }}
        >
          <Table stickyHeader aria-label="price references table">
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellStyle}>Produto</TableCell>
                <TableCell sx={headerCellStyle}>Preço Atual</TableCell>
                <TableCell sx={headerCellStyle}>P. Interno</TableCell>
                <TableCell sx={headerCellStyle}>P. Sugerido</TableCell>
                <TableCell sx={headerCellStyle}>Taxa Venda</TableCell>
                <TableCell sx={headerCellStyle}>Frete</TableCell>
                <TableCell sx={headerCellStyle}>Comp.</TableCell>
                <TableCell sx={headerCellStyle}>Diferença</TableCell>
                <TableCell sx={headerCellStyle}>P. Médio</TableCell>
                <TableCell sx={headerCellStyle}>Atualizado</TableCell>
                <TableCell sx={headerCellStyle}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.length > 0 ? (
                data.map((product, index) => (
                  <TableRow
                    key={product._id || index}
                    sx={{
                      backgroundColor: selectedProduct?.item_id === product.item_id 
                        ? 'rgba(255, 112, 67, 0.08)' 
                        : index % 2 === 0 ? '#fefefe' : '#ffffff',
                      borderLeft: selectedProduct?.item_id === product.item_id 
                        ? '3px solid #ff7043' 
                        : '3px solid transparent',
                      '&:hover': { backgroundColor: '#f8fafc' },
                    }}
                  >
                    {/* Produto */}
                    <TableCell sx={{ ...cellStyle, textAlign: 'left', minWidth: '180px' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                        {product.item_id}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                        R: {formatNumber(product.ratio || 0, 1)}
                      </Typography>
                    </TableCell>

                    {/* Preço Atual */}
                    <TableCell sx={cellStyle}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        {getTrendIcon(product.percent_difference || 0)}
                        <Typography sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                          {formatBRL(product.current_price?.amount || 0)}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Preço Interno */}
                    <TableCell sx={cellStyle}>
                      <Typography sx={{ color: '#8b5cf6', fontSize: '0.75rem', fontWeight: 500 }}>
                        {formatBRL(product.internal_price?.amount || 0)}
                      </Typography>
                    </TableCell>

                    {/* Preço Sugerido */}
                    <TableCell sx={cellStyle}>
                      <Typography sx={{ color: '#22c55e', fontSize: '0.75rem', fontWeight: 500 }}>
                        {formatBRL(product.suggested_price?.amount || 0)}
                      </Typography>
                    </TableCell>

                    {/* Taxa de Venda */}
                    <TableCell sx={cellStyle}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
                        {formatBRL(product.costs?.selling_fees || 0)}
                      </Typography>
                    </TableCell>

                    {/* Frete */}
                    <TableCell sx={cellStyle}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
                        {formatBRL(product.costs?.shipping_fees || 0)}
                      </Typography>
                    </TableCell>

                    {/* Competidores */}
                    <TableCell sx={cellStyle}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 500 }}>
                        {product.metadata?.compared_values || 0}
                      </Typography>
                    </TableCell>

                    {/* Diferença */}
                    <TableCell sx={cellStyle}>
                      <Typography 
                        sx={{ 
                          color: (product.percent_difference || 0) > 0 ? '#dc2626' : '#059669',
                          fontWeight: 600,
                          fontSize: '0.75rem'
                        }}
                      >
                        {(product.percent_difference || 0) > 0 ? '+' : ''}{formatNumber(product.percent_difference || 0, 1)}%
                      </Typography>
                    </TableCell>

                    {/* Status */}
                    <TableCell sx={cellStyle}>
                      <Chip
                        label={getStatusChip(product.status || '')}
                        color={getStatusColor(product.status || '')}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: '20px' }}
                      />
                    </TableCell>

                    {/* Atualizado */}
                    <TableCell sx={cellStyle}>
                      <Tooltip title={formatDateTime(product.last_updated)} arrow placement="top">
                        <Typography 
                          sx={{ 
                            fontSize: '0.7rem', 
                            color: '#64748b',
                            cursor: 'help',
                            '&:hover': { color: '#475569' }
                          }}
                        >
                          {formatDate(product.last_updated)}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    {/* Ações */}
                    <TableCell sx={cellStyle}>
                      <IconButton
                        size="small"
                        onClick={() => onSelectProduct(product)}
                        sx={{
                          backgroundColor: '#f8fafc',
                          '&:hover': { backgroundColor: '#e2e8f0' },
                          padding: '4px'
                        }}
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : !isLoading ? (
                <TableRow>
                  <TableCell colSpan={11} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Nenhum produto encontrado
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Paginação */}
        {pagination && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mt: 3,
            flexWrap: 'wrap',
            gap: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Itens por página</InputLabel>
                <Select
                  value={pagination.limit}
                  label="Itens por página"
                  onChange={(e) => onLimitChange(e.target.value)}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary">
                {pagination.total} produtos encontrados
              </Typography>
            </Box>

            {pagination.totalPages > 1 && (
              <Pagination
                count={pagination.totalPages}
                page={pagination.currentPage}
                onChange={(_, page) => onPageChange(page)}
                color="primary"
                size="medium"
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: '8px',
                    fontWeight: 500,
                    '&.Mui-selected': {
                      backgroundColor: '#ff7043',
                      color: 'white'
                    }
                  }
                }}
              />
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}